import { store } from 'redux/store';
import i18n from 'i18n/config';
import { useCallback, useEffect, useState } from 'react';

import { PartnerCategory } from 'types';

import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';
import { getPartnerCategories } from 'modules/partners/redux/partnerCategoryActions';

type UseGetCategoryOptionsProps = {
  societyId?: string;
};

const mapOption = (category: PartnerCategory): CustomAsyncSelectOption => ({
  value: category?._id,
  label: `${i18n.t(category?.name)} (${category?.partnersCount?.total ?? 0})`,
  name: i18n.t(category?.name),
  partnersCount: category?.partnersCount,
});

const mapOptions = (categories: PartnerCategory[]): CustomAsyncSelectOption[] =>
  categories
    .filter(
      (category) => category?.partnersCount && category.partnersCount.total > 0
    )
    .map((category) => mapOption(category));

export const useGetCategoryOptions = ({
  societyId,
}: UseGetCategoryOptionsProps) => {
  const [selectedCategoryOption, setSelectedCategoryOption] =
    useState<CustomAsyncSelectOption | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<
    CustomAsyncSelectOption[]
  >([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const handleChangeCategoryOption = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    setSelectedCategoryOption(selectedOption);
  };

  const fetchCategories = useCallback(async () => {
    if (!societyId) return;
    setIsLoading(true);
    try {
      const categories = await store.dispatch(getPartnerCategories(societyId));

      setIsLoading(false);

      const mappedCategoryOptions = mapOptions(categories);

      setCategoryOptions(mappedCategoryOptions);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
    }
  }, [societyId]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return {
    data: {
      selectedCategoryOption,
      categoryOptions,
    },
    isLoading,
    isError,
    actions: {
      handleChangeCategoryOption,
    },
  };
};
